import { displayKeywords } from '../../../domain/keywords';
import { recordKey } from '../../collection/multiCollectionIdentity';
import { Box, ListItem } from '@mui/material';
import { TagPill } from '../../sharing/TagPill';
import { CSSProperties, memo, useCallback } from 'react';
import { Item } from '../../../domain/item';
import {
  ItemListCardContent,
  itemListCardStyles,
} from '../../../components/ItemListCardContent';
import {
  CardSize,
  DISPLAY_SIZE_PROFILES,
} from '../../../hooks/useUiPreferences';
import { CollectionSelectionController } from '../../collection/types';
import { useCollectionEntryInteraction } from '../../collection/hooks/useCollectionEntryInteraction';

export const ItemListRow = memo(function ItemListRow({
  item,
  showLocation = true,
  showQuantity = true,
  cardSize,
  selection,
  onOpen,
  itemPosition,
  totalItemCount,
  visibleIds,
  virtualStyle,
}: {
  item: Item & { collectionName?: string };
  showLocation?: boolean;
  showQuantity?: boolean;
  cardSize: CardSize;
  selection: CollectionSelectionController;
  onOpen: (item: Item) => void;
  itemPosition?: number;
  totalItemCount?: number;
  visibleIds?: string[];
  virtualStyle?: CSSProperties;
}) {
  const size = DISPLAY_SIZE_PROFILES[cardSize];
  const openItem = useCallback(() => onOpen(item), [item, onOpen]);
  const interaction = useCollectionEntryInteraction({
    id: recordKey(item),
    label: displayKeywords(item.keywords),
    selection,
    onOpen: openItem,
    visibleIds,
  });

  const metadata = [
    showLocation
      ? `${item.collectionName ? `${item.collectionName}/` : ''}${item.location || 'No location'}`
      : undefined,
    showQuantity ? `Quantity ${item.qty || 0}` : undefined,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <ListItem
      disablePadding
      aria-setsize={totalItemCount}
      aria-posinset={itemPosition}
      data-testid={`item-list-row-${item.id}`}
      data-item-id={recordKey(item)}
      style={virtualStyle}
      sx={{
        minHeight: size.itemListRowHeight,
        bgcolor: interaction.selected ? 'action.selected' : 'background.paper',
        ...itemListCardStyles,
        contentVisibility: 'auto',
        containIntrinsicSize: `auto ${size.itemListRowHeight}px`,
      }}
    >
      <ItemListCardContent
        item={item}
        title={displayKeywords(item.keywords)}
        metadata={
          <>
            {metadata}
            {item.displayTags?.map((tag, index) => (
              <Box
                component="span"
                key={tag.tagId}
                sx={{
                  ml: metadata ? (index === 0 ? 1 : 0.5) : index > 0 ? 0.5 : 0,
                }}
              >
                <TagPill tag={tag} />
              </Box>
            ))}
          </>
        }
        height={size.itemListRowHeight}
        imageWidth={size.itemListImageSize}
        imageTestId={`item-list-image-${item.id}`}
        textGap={size.itemListGap}
        actionProps={interaction.actionProps}
        primaryTypographyProps={{
          variant: 'body2',
          fontWeight: interaction.selected ? 700 : 600,
          noWrap: true,
        }}
        secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
        checkbox={
          selection.active
            ? {
                checked: interaction.selected,
                label: `${interaction.selected ? 'Deselect' : 'Select'} ${displayKeywords(item.keywords)}`,
                onToggle: interaction.toggle,
              }
            : undefined
        }
      />
    </ListItem>
  );
});
